import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Table from "react-bootstrap/Table"
import Button from "react-bootstrap/Button"
import { FaPlus } from "react-icons/fa"
import { FiSettings, FiTrash2 } from "react-icons/fi"

import useT from "utils/hooks/useT"
import { ConfigContainer, Popup } from "components"
import { Container, Title, Text, Select } from "components/Form"
import {
  limsProductTestPlan,
  limsForskrifter,
  limsSpecs,
  limsSstTemplates,
  limsWorklistTemplates,
  analysisDefinitions,
  sampleView,
  message,
} from "state_management"

// Product test plan curation (F-B2). QC defines an authoritative set of analyses (and thus
// their F-B1 specs) per product, independent of sample history. The backend's auto-tracked
// product→analysis relationships (sampleView.productAnalysis) are shown as *suggestions*.
//
// The master list is the union of products that have a curated plan and products seen on
// samples. The editor lets QC pick the curated analysis set (with spec ✓/⚠ status), promote
// suggestions into the plan, and clean stray auto-tracked relationships. The whole LimsScreen
// is already gated behind showLims, so no extra flag check is needed here.
export default function ProductTestPlan() {
  const { t } = useT("common")
  const dispatch = useDispatch()

  // null = closed; { productName: "", isNew: true } = adding; { productName, isNew: false } = editing.
  const [editing, setEditing] = useState(null)
  // Product name pending a delete confirmation (the row-level "delete the whole plan" action).
  const [confirmDeleteName, setConfirmDeleteName] = useState(null)

  const plannedNames = useSelector(limsProductTestPlan.selectPlannedProductNames)
  const productAnalysis = useSelector((state) => state.sampleView.productAnalysis)
  const plans = useSelector((state) => state.limsProductTestPlan)
  // SPEC-067 (#4170): readiness asks whether a product can be TESTED, and nothing can be tested
  // against master data nobody has approved. Every column below therefore counts APPROVED, in-force
  // versions — not content someone has typed. For the specification that is `effectiveVersionNumber`
  // on the analysis-specifications row: the same field that menu bases its own status column on, so
  // the two screens cannot disagree.
  const specRows = useSelector((state) => state.limsSpecs.list)
  // Effective method documents (forskrifter) and per-definition review specs feed the readiness
  // columns below; read the whole slices and resolve per-definition via their selectors.
  const forskrifter = useSelector(limsForskrifter.selectDocuments)
  const reviewSpecMap = useSelector((state) => state.limsReviewSpec)
  // SPEC-067: the two new columns. The SST side needs the definition's own SST spec (does it
  // require one, and which template is its default) and the template library to resolve that
  // default's state; the worklist side needs the templates linked to each definition.
  const sstSpecMap = useSelector((state) => state.limsSstSpec)
  const sstTemplates = useSelector(limsSstTemplates.selectSstTemplates)
  const worklistTemplates = useSelector(limsWorklistTemplates.selectWorklistTemplates)

  useEffect(() => {
    dispatch(limsProductTestPlan.fetch())
    // SPEC-021 (B2): the QC + review specs ride on the analysis-definition payload — one
    // definitions fetch hydrates both spec slices (the spec ✓/⚠ and reviewer/duration columns).
    dispatch(analysisDefinitions.fetch())
    // SPEC-067: the analysis-specifications list carries `effectiveVersionNumber` — whether a
    // specification is actually in force, which the definitions payload alone cannot say.
    dispatch(limsSpecs.fetch())
    // Forskrifter (effective method docs) back the analysis-document column.
    dispatch(limsForskrifter.fetch())
    // SPEC-067: the SST and worklist columns.
    dispatch(limsSstTemplates.fetch())
    dispatch(limsWorklistTemplates.fetch())
    // Ensure the auto-tracked product→analysis relationships are loaded. The sample-view
    // fetch needs no caller params (it computes its own window), so it is safe to dispatch
    // when the data is missing.
    if (!productAnalysis) {
      dispatch(sampleView.fetch())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  // Master list: union of curated product names and productAnalysis keys, sorted. Empty /
  // null product keys (samples with no product name) are skipped — they cannot be curated.
  const products = useMemo(() => {
    const names = new Set()
    plannedNames.forEach((n) => names.add(n))
    Object.keys(productAnalysis || {}).forEach((n) => {
      if (n && n !== "null" && n !== "undefined") names.add(n)
    })
    return [...names].sort((a, b) => a.localeCompare(b))
  }, [plannedNames, productAnalysis])

  // SPEC-067: definition ids whose SPECIFICATION is in force. A draft awaiting two signatures is
  // not readiness — it is the opposite of it. The predicate lives in the specs slice so this table
  // and the plan editor below it cannot answer differently about the same analysis.
  const defsWithEffectiveSpec = useMemo(() => limsSpecs.effectiveSpecIdsFrom(specRows), [specRows])

  // Set of analysis-definition ids that have an EFFECTIVE method document linked to them. Built
  // once from the forskrifter slice so the per-product readiness pass is a cheap membership test
  // instead of calling selectEffectiveForDefinition per (product, definition) pair.
  const defsWithEffectiveDoc = useMemo(() => {
    const out = new Set()
    const docs = forskrifter || []
    docs.forEach((doc) => {
      const hasEffective = (doc.versions || []).some(
        (v) => v.state === limsForskrifter.FORSKRIFT_STATES.EFFECTIVE
      )
      if (hasEffective) {
        const defIds = doc.analysisDefinitionIds || []
        defIds.forEach((defId) => out.add(defId))
      }
    })
    return out
  }, [forskrifter])

  const effectiveSstTemplateIds = useMemo(
    () =>
      new Set(
        (sstTemplates || [])
          .filter((tpl) => tpl && tpl.state === limsSstTemplates.SST_TEMPLATE_STATES.EFFECTIVE)
          .map((tpl) => tpl.id)
      ),
    [sstTemplates]
  )

  // SPEC-067: is this analysis's SST side settled? An analysis that does not require an SST counts
  // as ready — it is not unfinished, it simply has no such step, and a denominator that changed
  // from column to column could not be read across the row. One that DOES require an SST needs a
  // default template naming criteria that are in force; the run-time panel has no picker (#4174),
  // so the definition's default is the only thing an analyst can be judged against.
  //
  // Asked per definition rather than built as a set of "settled" ids on purpose: the SST slice only
  // holds an entry for a definition that HAS an SST spec row, so a set built by walking it would
  // silently drop every analysis that has no SST at all — the ones that most obviously have nothing
  // outstanding.
  const sstSettled = useMemo(() => {
    const specs = sstSpecMap || {}
    return (defId) => {
      const sstSpec = specs[defId]
      if (!sstSpec || !sstSpec.requiresSst) return true
      return !!(sstSpec.instrumentType && effectiveSstTemplateIds.has(sstSpec.defaultSstTemplateId))
    }
  }, [sstSpecMap, effectiveSstTemplateIds])

  // SPEC-067: definition ids with an in-force worklist template OF THEIR OWN. The generic template
  // (analysisDefinitionId null) is a run-time fallback, not preparation — a product covered only by
  // the fallback has not had its sample prep written down.
  const defsWithEffectiveWorklist = useMemo(() => {
    const out = new Set()
    ;(worklistTemplates || []).forEach((tpl) => {
      if (
        tpl &&
        tpl.analysisDefinitionId &&
        tpl.state === limsWorklistTemplates.WORKLIST_TEMPLATE_STATES.EFFECTIVE
      ) {
        out.add(tpl.analysisDefinitionId)
      }
    })
    return out
  }, [worklistTemplates])

  // Readiness per product across its curated analyses: how many have an in-force specification, an
  // effective method document and a reviewer competence group. Computed from the slice maps
  // directly so we don't call the combined selectors per row.
  //
  // SPEC-067 dropped two columns that used to sit here: whether the specification had a CALCULATED
  // parameter, and whether a review DURATION was set. Neither is a condition for testing a product
  // — they widened the row without changing the screen's one answer — and both are still on the
  // analysis definition, where they are set.
  const readiness = useMemo(() => {
    const out = {}
    products.forEach((name) => {
      const ids = (plans[name] && plans[name].analysisDefinitionIds) || []
      const withSpec = ids.filter((defId) => defsWithEffectiveSpec.has(defId)).length
      const withDoc = ids.filter((defId) => defsWithEffectiveDoc.has(defId)).length
      const withReviewerGroup = ids.filter(
        (defId) => (reviewSpecMap[defId]?.allowedGroupIds?.length || 0) > 0
      ).length
      const withSst = ids.filter(sstSettled).length
      const withWorklist = ids.filter((defId) => defsWithEffectiveWorklist.has(defId)).length
      out[name] = {
        total: ids.length,
        withSpec,
        withDoc,
        withReviewerGroup,
        withSst,
        withWorklist,
      }
    })
    return out
  }, [
    products,
    plans,
    defsWithEffectiveSpec,
    defsWithEffectiveDoc,
    reviewSpecMap,
    sstSettled,
    defsWithEffectiveWorklist,
  ])

  const handleClose = () => setEditing(null)

  // Delete a product from the table with everything under it — its curated plan (if any) AND
  // its auto-tracked product→analysis relationships — so the row disappears. Confirmed first.
  const handleDelete = (productName) => {
    const ops = []
    if (plannedNames.includes(productName)) {
      ops.push(dispatch(limsProductTestPlan.removeForProduct(productName)))
    }
    const relationships = (productAnalysis && productAnalysis[productName]) || []
    relationships.forEach((rel) => {
      if (rel && rel.id) ops.push(dispatch(sampleView.removeProductAnalysis(rel.id)))
    })
    Promise.all(ops)
      .then(() => dispatch(message.success(t("limsProductPlanDeleted", { name: productName }))))
      .catch(() => dispatch(message.error(t("limsProductPlanDeleteFailed", { name: productName }))))
    setConfirmDeleteName(null)
  }

  return (
    <div className="LimsProducts">
      <div className="LimsProducts-header">
        <h1 className="LimsProducts-title">{t("limsProductReadiness")}</h1>
        <Button variant="primary" onClick={() => setEditing({ productName: "", isNew: true })}>
          <FaPlus /> {t("limsProductAdd")}
        </Button>
      </div>

      {products.length === 0 ? (
        <p className="LimsProducts-empty">{t("limsProductsNoEntries")}</p>
      ) : (
        <Table striped hover responsive className="LimsProducts-table">
          <thead>
            <tr>
              <th>{t("limsProductName")}</th>
              <th>{t("limsProductCuratedCount")}</th>
              <th>{t("limsProductSpecReadiness")}</th>
              <th>{t("limsProductDocsReadiness")}</th>
              <th>{t("limsProductReviewerGroupReadiness")}</th>
              <th>{t("limsProductSstReadiness")}</th>
              <th>{t("limsProductWorklistReadiness")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {products.map((name) => {
              const hasPlan = plannedNames.includes(name)
              const r = readiness[name] || {
                total: 0,
                withSpec: 0,
                withDoc: 0,
                withReviewerGroup: 0,
                withSst: 0,
                withWorklist: 0,
              }
              const showCounts = hasPlan && r.total > 0
              return (
                <tr key={name}>
                  <td>{name}</td>
                  <td>{hasPlan ? r.total : t("limsProductNoPlan")}</td>
                  <td>
                    {showCounts
                      ? t("limsProductSpecReady", { withSpec: r.withSpec, total: r.total })
                      : "—"}
                  </td>
                  <td>
                    {showCounts
                      ? t("limsProductDocsReady", { ready: r.withDoc, total: r.total })
                      : "—"}
                  </td>
                  <td>
                    {showCounts
                      ? t("limsProductReviewerGroupReady", {
                          ready: r.withReviewerGroup,
                          total: r.total,
                        })
                      : "—"}
                  </td>
                  <td>
                    {showCounts ? t("limsProductSstReady", { ready: r.withSst, total: r.total }) : "—"}
                  </td>
                  <td>
                    {showCounts
                      ? t("limsProductWorklistReady", { ready: r.withWorklist, total: r.total })
                      : "—"}
                  </td>
                  <td className="LimsProducts-actions">
                    <div className="LimsProducts-actions-inner">
                      <FiSettings
                        className="LimsProducts-icon"
                        title={t("edit")}
                        onClick={() => setEditing({ productName: name, isNew: false })}
                      />
                      <FiTrash2
                        className="LimsProducts-icon LimsProducts-icon--danger"
                        title={t("delete")}
                        onClick={() => setConfirmDeleteName(name)}
                      />
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}

      {editing && (
        <ProductPlanEditor
          productName={editing.productName}
          isNew={editing.isNew}
          existingNames={products}
          onClose={handleClose}
        />
      )}

      {confirmDeleteName && (
        <Popup nonScrollable onCancel={() => setConfirmDeleteName(null)}>
          <div className="LimsProducts-confirm-dialog">
            <p>{t("limsProductConfirmDelete", { name: confirmDeleteName })}</p>
            <div className="LimsProducts-confirm-actions">
              <Button variant="secondary" onClick={() => setConfirmDeleteName(null)}>
                {t("cancel")}
              </Button>
              <Button variant="danger" onClick={() => handleDelete(confirmDeleteName)}>
                {t("delete")}
              </Button>
            </div>
          </div>
        </Popup>
      )}
    </div>
  )
}


// Editor popup: pick the curated analysis set (multi-select with spec status) and reconcile
// suggestions. Mirrors ConsumablesRegister's in-file ConfigContainer + Popup pattern.
function ProductPlanEditor({ productName: initialName, isNew, existingNames, onClose }) {
  const { t } = useT("common")
  const dispatch = useDispatch()

  const [productName, setProductName] = useState(initialName || "")
  const [reason, setReason] = useState("")

  // The combined view for the current product name. For a brand-new product the name may be
  // empty until typed; selectProductTestPlan returns an empty curated/empty-or-historic
  // suggestions set in that case.
  const combined = useSelector((state) =>
    limsProductTestPlan.selectProductTestPlan(state, productName)
  )
  const definitions = useSelector((state) => state.analysisDefinitions)
  const specRows = useSelector((state) => state.limsSpecs.list)
  const plannedNames = useSelector(limsProductTestPlan.selectPlannedProductNames)

  // Local working copy of the curated analysis-definition ids; seed from the stored plan.
  const [selectedIds, setSelectedIds] = useState(() => combined.curated.map((c) => c.analysisDefinitionId))

  // The analysis-definition collection for the multi-select ({ id, name }).
  const definitionCollection = useMemo(
    () =>
      Object.values(definitions || {})
        .filter((d) => d && d.name)
        .map((d) => ({ id: d.id, name: d.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [definitions]
  )

  // SPEC-067: the badge answers the same question as the table behind this popup — is the
  // specification IN FORCE — so the two cannot contradict each other on one screen. It used to ask
  // whether the spec had any content, which reads "defined" for a draft the table counts as not
  // ready.
  const effectiveSpecIds = useMemo(() => limsSpecs.effectiveSpecIdsFrom(specRows), [specRows])
  const isSpecDefined = (defId) => effectiveSpecIds.has(defId)

  const handleSubmit = () => {
    const trimmed = (productName || "").trim()
    if (!trimmed) {
      dispatch(message.error(t("limsProductNameRequired")))
      return false
    }
    // Removing every analysis and saving deletes the plan record — the master table has no
    // delete action, so the editor is the single place to manage the plan and its analyses.
    if (selectedIds.length === 0) {
      if (plannedNames.includes(trimmed)) {
        dispatch(limsProductTestPlan.removeForProduct(trimmed))
          .then(() => {
            dispatch(message.success(t("limsProductPlanDeleted", { name: trimmed })))
            onClose()
          })
          .catch(() => {
            dispatch(message.error(t("limsProductPlanDeleteFailed", { name: trimmed })))
          })
      } else {
        onClose()
      }
      return true
    }
    const wasNew = !existingNames.includes(trimmed)
    dispatch(limsProductTestPlan.setForProduct(trimmed, selectedIds, reason.trim() || null, wasNew))
      .then(() => {
        dispatch(message.success(t("limsProductPlanSaved", { name: trimmed })))
        onClose()
      })
      .catch(() => {
        dispatch(message.error(t("limsProductPlanSaveFailed", { name: trimmed })))
      })
    return true
  }

  return (
    <Popup nonScrollable onCancel={onClose}>
      <ConfigContainer
        title={isNew ? t("limsProductAdd") : t("limsProductEdit", { name: initialName })}
        cancelHandler={onClose}
        submitHandler={handleSubmit}
        className="LimsProducts-editor"
      >
        {isNew ? (
          <Container className="LimsProducts-field">
            <Title>
              {t("limsProductName")} <div className="red-text">*</div>
            </Title>
            <Text
              value={productName}
              required
              onChange={setProductName}
              placeholder={t("limsProductNamePlaceholder")}
            />
          </Container>
        ) : (
          <h3 className="LimsProducts-product-title">{productName}</h3>
        )}

        <div className="LimsProducts-field">
          <h4 className="LimsProducts-section-heading">{t("limsProductCuratedPlan")}</h4>
          <Select
            isMulti
            collection={definitionCollection}
            value={selectedIds}
            onChange={(values) => setSelectedIds(values || [])}
            menuPlacement="bottom"
          />
          {selectedIds.length > 0 && (
            <ul className="LimsProducts-spec-list">
              {selectedIds.map((defId) => {
                const def = definitions[defId]
                const defined = isSpecDefined(defId)
                return (
                  <li key={defId} className="LimsProducts-spec-item">
                    <span className="LimsProducts-spec-name">{def ? def.name : defId}</span>
                    <span className="LimsProducts-spec-right">
                      <span className={"LimsProducts-spec-badge " + (defined ? "is-defined" : "is-missing")}>
                        {defined ? t("limsProductSpecDefined") : t("limsProductSpecMissing")}
                      </span>
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <Container className="LimsProducts-field">
          <Title>{t("limsAuditReason")}</Title>
          <Text
            value={reason}
            onChange={setReason}
            placeholder={t("limsProductReasonPlaceholder")}
          />
        </Container>
      </ConfigContainer>
    </Popup>
  )
}
